import { FC, useState, useEffect } from 'react';
import {
    TextField as MuiTextField,
    InputAdornment,
    MenuItem,
    Select,
    Box,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import PhoneIcon from '@mui/icons-material/Phone';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';


export interface FormPhoneInputProps {
    name: string;
    label: string;
    placeholder?: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    defaultCountry?: CountryCode;
    showCountrySelect?: boolean;
    showSuccessIcon?: boolean;
    showErrorIcon?: boolean;
    onCountryChange?: (country: CountryCode) => void;
}

const popularCountries: { code: CountryCode; name: string; flag: string }[] = [
    { code: 'MD', name: 'Moldova', flag: '🇲🇩' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴' },
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺' },
];

export const FormPhoneInput: FC<FormPhoneInputProps> = ({
    name,
    label,
    placeholder = '69123456',
    helperText,
    required = false,
    disabled = false,
    defaultCountry = 'MD',
    showCountrySelect = true,
    showSuccessIcon = true,
    showErrorIcon = true,
    onCountryChange,
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);

    const error = errors[name];
    const hasError = !!error;
    const errorMessage = error?.message as string;

    const isValid = !hasError && control._formState.touchedFields[name];

    useEffect(() => {
        if (onCountryChange) {
            onCountryChange(selectedCountry);
        }
    }, [selectedCountry, onCountryChange]);

    const handleCountryChange = (country: CountryCode) => {
        setSelectedCountry(country);
    };

    // Get country calling code (e.g., "373" for MD)
    const getCountryCode = (country: CountryCode): string => {
        try {
            return getCountryCallingCode(country);
        } catch {
            return '';
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, onBlur, value } }) => {
                // Extract phone number without country code for display
                const countryCode = getCountryCode(selectedCountry);
                const fullPrefix = `+${countryCode}`;

                // If value starts with country code, remove it for display
                let displayValue = value || '';
                if (displayValue.startsWith(fullPrefix)) {
                    displayValue = displayValue.substring(fullPrefix.length).trim();
                } else if (displayValue.startsWith('+')) {
                    // Remove any other country code
                    displayValue = displayValue.replace(/^\+\d+\s*/, '');
                }

                const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    let inputValue = e.target.value;

                    // Remove any non-numeric characters
                    let numericValue = inputValue.replace(/\D/g, '');

                    // If input starts with 0, remove it (not allowed)
                    if (numericValue.startsWith('0')) {
                        numericValue = numericValue.substring(1);
                    }

                    // Limit to 15 digits
                    if (numericValue.length > 15) {
                        numericValue = numericValue.substring(0, 15);
                    }

                    // Store as: +{countryCode}{phoneNumber}
                    const fullNumber = numericValue ? `+${countryCode}${numericValue}` : '';
                    onChange(fullNumber);
                };

                return (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        {showCountrySelect && (
                            <Select
                                value={selectedCountry}
                                onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
                                disabled={disabled}
                                sx={{
                                    width: '120px',
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    },
                                }}
                                size="medium"
                            >
                                {popularCountries.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{country.flag}</span>
                                            <span>{country.code}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        )}

                        <MuiTextField
                            value={displayValue}
                            onChange={handleChange}
                            onBlur={onBlur}
                            label={label}
                            placeholder={placeholder}
                            required={required}
                            disabled={disabled}
                            type="tel"
                            fullWidth
                            error={hasError}
                            helperText={hasError ? errorMessage : helperText}
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon color="action" sx={{ mr: 0.5 }} />
                                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            {fullPrefix}
                                        </Box>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {showErrorIcon && hasError && (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" fontSize="small" />
                                            </InputAdornment>
                                        )}
                                        {showSuccessIcon && isValid && (
                                            <InputAdornment position="end">
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            </InputAdornment>
                                        )}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderWidth: 2,
                                    },
                                    '&.Mui-error fieldset': {
                                        borderWidth: 2,
                                    },
                                },
                            }}
                        />
                    </Box>
                );
            }}
        />
    );
};

